// src/pages/weekly_demand/WeeklyDemandPlanner.jsx

import React, { useState } from 'react';
import mrpService from 'services/mrpService';
import './WeeklyDemandPlanner.css';

/**
 * WeeklyDemandPlanner
 * - Uses SKU code as PK (string). So we do NOT parse it as int.
 * - If a plan doesn't exist, Django creates it automatically (DemandBatch).
 * - Each cell is an input => typed quantity => single POST => create demands.
 */
function WeeklyDemandPlanner() {
  const currentYear = new Date().getFullYear();
  const [planName, setPlanName] = useState('');
  const [year, setYear] = useState(String(currentYear));

  const [monthMap, setMonthMap] = useState({});
  const [fgList, setFgList] = useState([]);
  const [existingUsage, setExistingUsage] = useState({});

  const [activeMonth, setActiveMonth] = useState(null);

  // For storing plan-level notes
  const [comments, setComments] = useState('');

  // UI states
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  /**
   * 1) Load the plan data from Django
   * GET /mrp/weekly-demand/?year=...&batch_name=...
   */
  async function handleLoadPlan() {
    try {
      setLoading(true);
      setError('');

      // fallback if planName is blank
      const finalName = planName.trim() || `Plan_${year}`;

      const response = await mrpService.fetchWeeklyDemandData({
        year,
        batch_name: finalName,
      });

      // Update state
      setPlanName(response.batch_name || finalName);
      setMonthMap(response.month_map || {});
      setFgList(response.fg_list || []);
      setExistingUsage(response.existing_usage || {});

      const months = Object.keys(response.month_map || {});
      if (months.length) {
        setActiveMonth(months[0]);
      } else {
        setActiveMonth(null);
      }
    } catch (err) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  /**
   * 2) Submit new demands => single pass over .demand-input => POST JSON => create demands
   */
  async function handleSubmitPlan() {
    try {
      setLoading(true);
      setError('');

      const finalPlanName = planName.trim() || `Plan_${year}`;

      // Gather typed inputs
      const newDemands = [];
      const inputs = document.querySelectorAll('.demand-input');
      inputs.forEach((inp) => {
        const valStr = inp.value.trim();
        if (!valStr) return; // skip blank
        const quantity = parseInt(valStr, 10);
        if (quantity > 0) {
          // data-fg is a string PK (sku_code). Do NOT parse it as int
          const fgId = inp.dataset.fg;   // e.g. "AS07411100000020"
          const dDate = inp.dataset.date; // "YYYY-MM-DD"

          newDemands.push({
            fg_id: fgId, // string pk
            demand_date: dDate,
            quantity,
          });
        }
      });

      const payload = {
        batch_name: finalPlanName,
        batch_notes: comments.trim(),
        new_demands: newDemands,
      };

      // Post to Django
      await mrpService.createWeeklyDemands(payload);

      // Reload so newly created demands appear
      await handleLoadPlan();
      setComments('');
    } catch (err) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="weekly-demand-container">
      <h2>Weekly Demand Planner</h2>

      {/* Plan Name + Year */}
      <div className="inputs-row">
        <div className="field-box">
          <label>Plan Name</label>
          <input
            type="text"
            placeholder="e.g. MyPlanQ3"
            value={planName}
            onChange={(e) => setPlanName(e.target.value)}
          />
        </div>
        <div className="field-box">
          <label>Year</label>
          <input
            type="number"
            min="2000"
            value={year}
            onChange={(e) => setYear(e.target.value)}
          />
        </div>
        <div className="field-box" style={{ alignSelf: 'flex-end' }}>
          <button onClick={handleLoadPlan} disabled={loading}>
            {loading ? 'Loading...' : 'Load Plan'}
          </button>
        </div>
      </div>

      {error && <div className="error-box">Error: {error}</div>}

      {/* If plan loaded => show months + table */}
      {Object.keys(monthMap).length ? (
        <>
          <div className="comments-box">
            <label>Comments/Description</label>
            <textarea
              rows={2}
              placeholder="e.g. Q3 forecast or special run"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>

          {/* Month tabs */}
          <div className="tabs">
            {Object.keys(monthMap).map((m) => (
              <button
                key={m}
                className={`tab-btn ${m === activeMonth ? 'active' : ''}`}
                onClick={() => setActiveMonth(m)}
              >
                {getMonthName(m)}
              </button>
            ))}
          </div>

          {activeMonth && (
            <div className="table-wrapper">
              <table className="demand-table">
                <thead>
                  <tr>
                    <th>Week Start</th>
                    {fgList.map((fg) => (
                      <th key={fg.pk}>{fg.sku_code}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthMap[activeMonth].map((weekItem) => {
                    const isoWk = weekItem.iso_week_num;
                    const monDate = weekItem.monday_date;
                    return (
                      <tr key={`${activeMonth}-${isoWk}`}>
                        <td>{`Week ${isoWk} (${formatDate(monDate)})`}</td>
                        {fgList.map((fg) => {
                          // existing usage
                          const usageKey = `sku_${fg.pk}`;
                          const existingQty =
                            existingUsage[monDate]?.[usageKey] || 0;
                          return (
                            <td key={fg.pk}>
                              <div className="cell-block">
                                {existingQty > 0 && (
                                  <span className="existing-qty">
                                    Existing: {existingQty}
                                  </span>
                                )}
                                <input
                                  type="number"
                                  min="0"
                                  className="demand-input"
                                  data-fg={fg.pk}      // string pk
                                  data-date={monDate}
                                  placeholder={
                                    existingQty > 0 ? '+Add' : 'Enter'
                                  }
                                />
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="submit-row">
            <button onClick={handleSubmitPlan} disabled={loading}>
              {loading ? 'Saving...' : 'Submit Plan'}
            </button>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', marginTop: '1rem', color: '#666' }}>
          (No plan loaded. Enter Plan Name &amp; Year, then click "Load Plan")
        </div>
      )}
    </div>
  );
}

/* -------- Helpers -------- */
function getMonthName(m) {
  const num = parseInt(m, 10) || 1;
  return new Date(2000, num - 1, 1).toLocaleString('default', { month: 'long' });
}
function formatDate(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d)) return isoStr;
  return d.toLocaleDateString('default', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default WeeklyDemandPlanner;
